import { Button } from '@getethos/ethos-design-system-v2';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Button as MuiButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useContext, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import * as yup from 'yup';
import { FetchService } from '../../common/fetch.service';
import { QACaseType } from '../../common/interfaces/IQaCase';
import { SnackBarType } from '../layout/interfaces/ISnacksProps';
import { SnackbarContext } from '../layout/SnackbarContext';
import { createQACaseEndPoint } from './api.endPoints';

interface CreateQACaseForm {
  caseType: QACaseType;
  reason: string;
}

interface InitiateQAButtonProps {
  policyId: number;
  product: string;
}

const createQACaseSchema = yup.object({
  caseType: yup
    .mixed<QACaseType>()
    .oneOf([QACaseType.ENGINE_RULES_REVIEW, QACaseType.AUTO_PIA_RULES_REVIEW])
    .required('Case type is required'),
  reason: yup.string().default(''),
});

export function InitiateQAButton({ policyId, product }: InitiateQAButtonProps) {
  const [openDialog, setOpenDialog] = useState(false);
  const queryClient = useQueryClient();
  const { openSnacks, setSnacks } = useContext(SnackbarContext);
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateQACaseForm>({
    resolver: yupResolver(createQACaseSchema),
    defaultValues: {
      caseType: QACaseType.ENGINE_RULES_REVIEW,
      reason: '',
    },
  });

  const { mutate: createQACase, isLoading: isCreating } = useMutation({
    mutationFn: async (data: CreateQACaseForm) => {
      const payload = {
        policyId,
        caseType: data.caseType,
        triggerSource: 'wb_manual' as const,
        reason: data.reason,
        product,
      };

      return FetchService.getInstance().post(createQACaseEndPoint(), {
        body: payload,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qaCases', policyId] });
      setOpenDialog(false);
      reset();
    },
    onError: (error: any) => {
      setSnacks([
        ...openSnacks,
        {
          message: error.message,
          type: SnackBarType.ALERT,
          id: uuidv4(),
        },
      ]);
    },
  });

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    reset();
  };

  const onSubmit = (data: CreateQACaseForm) => {
    createQACase(data);
  };

  return (
    <>
      <span>
        <Button
          variant="infoOutline"
          buttonTitle="Initiate QA"
          onClick={handleOpenDialog}
          size="sm"
        />
      </span>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create QA Case</DialogTitle>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Controller
                name="caseType"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.caseType}>
                    <InputLabel>Case Type</InputLabel>
                    <Select {...field} label="Case Type">
                      <MenuItem value={QACaseType.ENGINE_RULES_REVIEW}>
                        Pre Issue
                      </MenuItem>
                      <MenuItem value={QACaseType.AUTO_PIA_RULES_REVIEW}>
                        Post Issue
                      </MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
              {errors.caseType && (
                <Typography color="error" variant="caption">
                  {errors.caseType.message}
                </Typography>
              )}

              <Controller
                name="reason"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Reason"
                    fullWidth
                    multiline
                    rows={3}
                    error={!!errors.reason}
                    helperText={errors.reason?.message}
                  />
                )}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <MuiButton onClick={handleCloseDialog}>Cancel</MuiButton>
            <MuiButton type="submit" variant="contained" disabled={isCreating}>
              {isCreating ? 'Creating...' : 'Create'}
            </MuiButton>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}
